import { Directive, ElementRef, EventEmitter, NgZone, OnInit, OnDestroy, Input, Output } from '@angular/core';
import { GoogleMapsService } from '@app/core/services/google-maps.service';

@Directive({
  selector: '[google-place]',
  standalone: true,
})
export class GooglePlacesDirective implements OnInit, OnDestroy {
  @Output() placeSelected = new EventEmitter<any>();

  /**
   * Sets the initial value without triggering the autocomplete dropdown.
   */
  @Input() set initialValue(val: string) {
    if (val) {
      // Small delay to ensure the Web Component is fully rendered before setting value
      setTimeout(() => this.setInternalValueDynamically(val, true), 150);
    }
  }

  private placeAutocomplete: any = null;
  private inputListener: any = null;
  private keyboardListener: any = null;
  private originalInput: HTMLInputElement | null = null;
  private internalGoogleInput: HTMLInputElement | null = null;

  // Flag to prevent re-opening popup after a selection is made
  private isSelecting = false;

  constructor(
    private readonly el: ElementRef,
    private readonly zone: NgZone,
    private readonly googleMapsService: GoogleMapsService
  ) {}

  ngOnInit(): void {
    // Defer initialization to avoid blocking app startup if API not configured
    Promise.resolve().then(() => this.initializeAutocomplete());
  }

  private async initializeAutocomplete(): Promise<void> {
    try {
      await this.googleMapsService.loadGoogleMaps();

      const google = (globalThis as any).google;

      if (!google?.maps?.places) {
        return;
      }

      // 1. Initialize Google Autocomplete Web Component
      this.placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
        types: ['establishment'],
      });

      // 2. Find the visible Material Input (sibling)
      const parent = this.el.nativeElement.parentNode;
      this.originalInput = parent.querySelector('input[matInput]');

      // 3. Inject Google component into DOM
      parent.insertBefore(this.placeAutocomplete, this.el.nativeElement);

      // 4. Hide the internal Google UI
      this.hideInternalGoogleUI();

      // 5. Setup Keyboard and Input synchronization
      if (this.originalInput) {
        // Disable Edge/Chrome autocomplete suggestions
        this.originalInput.setAttribute('autocomplete', 'one-time-code');
        this.originalInput.setAttribute('data-edge-ignore', 'true');
        this.originalInput.setAttribute('disableautocomplete', '');

        this.setupKeyboardProxy();

        this.originalInput.addEventListener('input', (e: any) => {
          if (!this.isSelecting) {
            this.setInternalValueDynamically(e.target.value, false);
          }
        });
      }

      // 6. Listen for the place selection event
      this.placeAutocomplete.addEventListener('gmp-select', this.handleSelect);

      this.inputListener = (event: any) => {
        if (!this.isSelecting) {
          this.syncToAngularFormControl(event.target.value);
        }
      };
      this.placeAutocomplete.addEventListener('input', this.inputListener);
    } catch (error) {
      // Initialization failed silently
    }
  }

  private hideInternalGoogleUI() {
    const isMobile = window.innerWidth <= 768;

    if (!isMobile) {
      const internal = this.findInternalInput();
      if (internal) {
        internal.style.display = 'none';
        const wrapper = internal.parentElement;
        if (wrapper) {
          wrapper.style.display = 'none';
        }
      }
    } else {
      this.placeAutocomplete.style.position = 'absolute';
      this.placeAutocomplete.style.height = '0px';
      this.placeAutocomplete.style.width = '0px';
      this.placeAutocomplete.style.overflow = 'hidden';
      this.placeAutocomplete.style.opacity = '0';
    }
  }

  private findInternalInput(): HTMLInputElement | null {
    // Guard: placeAutocomplete must be initialized
    if (!this.placeAutocomplete) {
      return null;
    }

    if (this.internalGoogleInput) {
      return this.internalGoogleInput;
    }

    try {
      const props = Object.getOwnPropertyNames(this.placeAutocomplete);
      for (const prop of props) {
        try {
          const val = this.placeAutocomplete[prop];
          if (val instanceof HTMLInputElement) {
            this.internalGoogleInput = val;
            return val;
          }
        } catch (e) {
          // Silently continue on property access error
        }
      }
    } catch (error) {
      // Error accessing properties, continue
    }
    return null;
  }

  private setupKeyboardProxy() {
    if (!this.originalInput) {
      return;
    }

    this.keyboardListener = (event: KeyboardEvent) => {
      const internal = this.findInternalInput();
      if (!internal || this.isSelecting) {
        return;
      }

      // Block Enter key if input is empty
      if (event.key === 'Enter') {
        const inputValue = this.originalInput?.value.trim();
        if (!inputValue) {
          event.preventDefault();
          return;
        }
      }

      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
        internal.focus();
        const clonedEvent = new KeyboardEvent('keydown', {
          key: event.key,
          code: event.code,
          bubbles: true,
          cancelable: true,
        });
        internal.dispatchEvent(clonedEvent);
      }
    };

    this.originalInput.addEventListener('keydown', this.keyboardListener);
  }

  private setInternalValueDynamically(val: string, isInitial = false) {
    // Guard: if placeAutocomplete is not ready yet, skip (initialValue setter might be called during init)
    if (!this.placeAutocomplete) {
      return;
    }

    // Don't forward empty values during user input (only allow during initial setup)
    if (!isInitial && (!val || val.trim().length === 0 || val.trim().length < 3)) {
      return;
    }

    const internal = this.findInternalInput();
    if (internal) {
      internal.value = val;
      if (!isInitial && !this.isSelecting) {
        internal.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  private readonly handleSelect = async (event: any) => {
    this.isSelecting = true;

    try {
      if (!event?.placePrediction) {
        this.isSelecting = false;
        return;
      }

      const place = event.placePrediction.toPlace();

      if (!place) {
        this.isSelecting = false;
        return;
      }

      await place.fetchFields({
        fields: ['displayName', 'addressComponents', 'nationalPhoneNumber', 'websiteURI'],
      });

      const mappedData = this.mapToExistingFormat(place);

      // Validate that we have a proper address
      if (!mappedData.address || mappedData.address.trim().length === 0) {
        console.warn('Invalid address from Google Places:', mappedData);
        this.isSelecting = false;
        return;
      }

      this.zone.run(() => {
        this.syncToAngularFormControl(mappedData.address);
        this.placeSelected.emit(mappedData);
        this.originalInput?.focus();
        setTimeout(() => {
          this.isSelecting = false;
        }, 300);
      });
    } catch (err) {
      console.error('Error selecting place:', err);
      this.isSelecting = false;
      // Silently fail - don't throw, just return
    }
  };

  private syncToAngularFormControl(value: string) {
    if (this.originalInput && this.originalInput.value !== value) {
      this.originalInput.value = value;
      this.originalInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private mapToExistingFormat(place: any) {
    const components = place.addressComponents || [];
    const addressObj: any = {};

    const typeMap: Record<string, { key: string; value: 'longText' | 'shortText' }> = {
      locality: { key: 'locality', value: 'longText' },
      postal_code: { key: 'postal_code', value: 'shortText' },
      country: { key: 'country', value: 'longText' },
      street_number: { key: 'street_number', value: 'shortText' },
      route: { key: 'route', value: 'longText' },
    };

    components.forEach((c: any) => {
      c.types.forEach((type: string) => {
        const map = typeMap[type];
        if (map) {
          addressObj[map.key] = c[map.value];
        }
      });
    });

    const name = place.displayName || '';
    const cleanStreet = `${addressObj.route || ''} ${addressObj.street_number || ''}`.trim();

    let displayValue = name;

    if (cleanStreet) {
      displayValue = `${name}, ${cleanStreet}`;
    }

    return {
      address: displayValue,
      postal_code: addressObj.postal_code || '',
      locality: addressObj.locality || '',
      country: addressObj.country || '',
      street_number: addressObj.street_number || '',
      phone_number: (place.nationalPhoneNumber || '').replace(/\D/g, ''),
      website: (place.websiteURI || '').replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, ''),
    };
  }

  ngOnDestroy(): void {
    if (this.placeAutocomplete) {
      this.placeAutocomplete.removeEventListener('gmp-select', this.handleSelect);
      this.placeAutocomplete.removeEventListener('input', this.inputListener);
      this.originalInput?.removeEventListener('keydown', this.keyboardListener);
      this.placeAutocomplete.remove();
    }
  }
}
